import React, { useContext, useMemo } from 'react';
import cn from 'classnames';
import { mergeProps } from '@react-aria/utils';
import { useButton as useButtonBase } from '@react-aria/button';
import { useHover } from '@react-aria/interactions';
import { Box } from '../box';
import { Typography } from '../typography';
import { SlotContext, useSlot } from '../slots';
import { useForwardedRef, usePatchedFocusRing, usePressMessage } from '../../hooks';
import styles from './styles.module.css';
export const ButtonBase = React.forwardRef(function ButtonBase({ children, className, classes = {}, shape = 'squircle', size, variant = 'default', hovered, pressed, focused, disabled, loading, Component = 'button', ...props }, ref) {
    const parentSlotContext = useContext(SlotContext);
    const finalSize = size || parentSlotContext.size;
    const slotContextValue = useMemo(() => ({ size: finalSize }), [finalSize]);
    const { heightClass, sizeClass } = useSlot({ size: finalSize });
    return (React.createElement(SlotContext.Provider, { value: slotContextValue },
        React.createElement(Box, { ...props, disabled: disabled, Component: Component, ref: ref, className: cn(className, sizeClass, heightClass, styles.button, styles[shape], styles[finalSize], variant && styles[variant], hovered && classes.hovered, pressed && classes.pressed, focused && classes.focused, disabled && classes.disabled, loading && classes.loading, {
                [styles.hovered]: hovered,
                [styles.pressed]: pressed,
                [styles.focused]: focused,
                [styles.disabled]: disabled,
                [styles.loading]: loading,
            }) }, children)));
    // forwardRef не умеет в дженерики :(
});
const SimpleButton = React.forwardRef(function SimpleButton({ children, textVariant = 'body2', ...props }, ref) {
    return (React.createElement(Button, { ref: ref, pl: "m", pr: "m", ...props },
        React.createElement(Typography.Icon, { variant: textVariant, justifyContent: "center", flex: 1, Component: "span" }, children)));
});
const LinkButton = React.forwardRef(function LinkButton({ href, target, textVariant = 'body2', children, ...props }, ref) {
    return (React.createElement(Button, { Component: "a", ref: ref, href: href, target: target, rel: target && 'noopener noreferrer', pl: "m", pr: "m", ...props },
        React.createElement(Typography.Icon, { variant: textVariant, justifyContent: "center", flex: 1, Component: "span" }, children)));
});
function useButton(props, ref) {
    const { onPress, onPressStart, onPressEnd, onPressChange, onPressUp, disabled, onFocus, onBlur, onKeyDown, onKeyUp, excludeFromTabOrder, ...rest } = props;
    // Через BoxConfig из пропсов стирается type, поэтому мини хак c целью достать. При использовании компонентов всё ок.
    // Элемент button приемлем потому что в других случаях будет просто undefined
    const { type } = props;
    const elRef = useForwardedRef(ref);
    const { isFocusVisible, focusProps } = usePatchedFocusRing();
    const { hoverProps, isHovered } = useHover({ isDisabled: disabled });
    // useButton не совместим с useOption
    const { isPressed, buttonProps } = useButtonBase({
        onPressStart: !props.loading ? onPressStart : undefined,
        onPressEnd: !props.loading ? onPressEnd : undefined,
        onPressChange,
        onPressUp: !props.loading ? onPressUp : undefined,
        onPress: !props.loading ? onPress : undefined,
        onFocus,
        onBlur,
        onKeyDown: e => {
            e.continuePropagation();
            onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        },
        onKeyUp: e => {
            e.continuePropagation();
            onKeyUp === null || onKeyUp === void 0 ? void 0 : onKeyUp(e);
        },
        isDisabled: disabled,
        type,
        excludeFromTabOrder,
    }, elRef);
    usePressMessage(props);
    return {
        elRef,
        isFocusVisible,
        isHovered,
        isPressed,
        buttonProps: mergeProps(rest, buttonProps, focusProps, hoverProps),
    };
}
export const Button = Object.assign(React.forwardRef(function Button(props, ref) {
    const { isHovered, isFocusVisible, isPressed, buttonProps, elRef } = useButton(props, ref);
    const isButtonElement = [undefined, 'button'].includes(buttonProps.Component);
    return (React.createElement(ButtonBase, { ref: elRef, ...buttonProps, type: isButtonElement ? buttonProps.type : props.type, pressed: isPressed, hovered: isHovered, focused: isFocusVisible }, props.children));
    // forwardRef не умеет в дженерики :(
}), {
    Simple: SimpleButton,
    Link: LinkButton,
});
